import {
  AnomalySeverity,
  AnomalySeverityType,
  DiffBucket,
  DiffBucketType,
  ProductType,
  ProductTypeType,
  SectionKey,
  SectionKeyType,
  SectionStatus,
  SectionStatusType
} from '@blisspointmedia/bpm-types/dist/MMMDataQA';

// Shared with bpm-lambdas via bpm-types; re-exported so existing imports keep working.
export { AnomalySeverity, DiffBucket, ProductType, SectionKey, SectionStatus };
export type {
  AnomalySeverityType,
  DiffBucketType,
  ProductTypeType,
  SectionKeyType,
  SectionStatusType
};

// A channel's demand group, abbreviated (summary tables, a split channel's reasons).
export const GROUP_SHORT_LABEL = { demandGeneration: 'DG', demandCapture: 'DC' } as const;

// A company can own several models (multi-model workspace). `ModelType` is the
// canonical, user-facing identity for each — distinct from the internal
// `ProductType` — used by the model switcher and capsules.
export const ModelType = {
  DTC: 'dtc',
  COMMERCE: 'commerce',
  GEO: 'geo',
  BRAND_EQUITY: 'brandEquity'
} as const;
export type ModelTypeType = (typeof ModelType)[keyof typeof ModelType];

export const MODEL_TYPE_LABELS: Record<ModelTypeType, string> = {
  [ModelType.DTC]: 'DTC MMM',
  [ModelType.COMMERCE]: 'Commerce MMM',
  [ModelType.GEO]: 'geoMMM',
  [ModelType.BRAND_EQUITY]: 'Brand Equity'
};

// Canonical display order for a company's models (switcher, summary, sorting).
export const MODEL_TYPE_ORDER: ModelTypeType[] = [
  ModelType.DTC,
  ModelType.COMMERCE,
  ModelType.GEO,
  ModelType.BRAND_EQUITY
];

export const PRODUCT_TYPE_TO_MODEL_TYPE: Record<ProductTypeType, ModelTypeType> = {
  [ProductType.MMM]: ModelType.DTC,
  [ProductType.COMMERCE]: ModelType.COMMERCE,
  [ProductType.GEO_MMM]: ModelType.GEO,
  [ProductType.BRAND_EQUITY]: ModelType.BRAND_EQUITY
};

// Order is cosmetic here (every consumer loops/checks the full set, none depends on
// sequence) -- kept matching the tab order (see STEPPER_STEPS) so there's only one
// "canonical order" to reason about, not two that quietly disagree.
export const SECTION_ORDER: SectionKeyType[] = [
  SectionKey.CAMPAIGNS,
  SectionKey.FEATURE_MAPPING,
  SectionKey.PORTFOLIO,
  SectionKey.CHANNELS,
  SectionKey.CONTEXT_VARS,
  SectionKey.DEPENDENT_VAR
];

export const SECTION_TITLES: Record<SectionKeyType, string> = {
  [SectionKey.PORTFOLIO]: 'Portfolio Overview',
  [SectionKey.CHANNELS]: 'Channel Review',
  [SectionKey.CAMPAIGNS]: 'Campaign Review',
  [SectionKey.FEATURE_MAPPING]: 'Feature Mapping',
  [SectionKey.CONTEXT_VARS]: 'Context Variables',
  [SectionKey.DEPENDENT_VAR]: 'Dependent Variable'
};

// Approve/flag review sections shown in the workspace summary (in order).
// Campaigns is a labeling gate, not an approve/flag section, so it's excluded;
// BPM Data Hygiene has been folded into the sections above. Feature Mapping is
// transparency-only (its own on-page copy says so) and dropped approve/flag
// entirely (Sean, 2026-08-27), so it's excluded here too now.
export const SUMMARY_SECTIONS: SectionKeyType[] = [
  SectionKey.PORTFOLIO,
  SectionKey.CHANNELS,
  SectionKey.CONTEXT_VARS,
  SectionKey.DEPENDENT_VAR
];

// Media Inputs reviews Portfolio and Channels together on one tab under one approve/flag,
// written to both persisted section keys at once (see media-inputs-utils).
export const MEDIA_INPUTS_SECTIONS: SectionKeyType[] = [SectionKey.PORTFOLIO, SectionKey.CHANNELS];

// What a reviewer approves or flags: Media Inputs (Portfolio + Channels), Context Variables,
// Dependent Variable. Frontend-only -- the backend still stores status per SectionKey.
export const ReviewUnit = {
  MEDIA_INPUTS: 'mediaInputs',
  CONTEXT_VARS: SectionKey.CONTEXT_VARS,
  DEPENDENT_VAR: SectionKey.DEPENDENT_VAR
} as const;
export type ReviewUnitType = (typeof ReviewUnit)[keyof typeof ReviewUnit];

export const REVIEW_UNITS: ReviewUnitType[] = [
  ReviewUnit.MEDIA_INPUTS,
  ReviewUnit.CONTEXT_VARS,
  ReviewUnit.DEPENDENT_VAR
];

export const REVIEW_UNIT_SECTIONS: Record<ReviewUnitType, SectionKeyType[]> = {
  [ReviewUnit.MEDIA_INPUTS]: MEDIA_INPUTS_SECTIONS,
  [ReviewUnit.CONTEXT_VARS]: [SectionKey.CONTEXT_VARS],
  [ReviewUnit.DEPENDENT_VAR]: [SectionKey.DEPENDENT_VAR]
};

export const REVIEW_UNIT_TITLES: Record<ReviewUnitType, string> = {
  [ReviewUnit.MEDIA_INPUTS]: 'Media Inputs',
  [ReviewUnit.CONTEXT_VARS]: 'Context Variables',
  [ReviewUnit.DEPENDENT_VAR]: 'Dependent Variable'
};

// Sections useSectionDataInvariance checks for "identical across models" -- every
// approve/flag section, plus Campaigns. Campaigns has no approve/flag status (it's
// excluded from SUMMARY_SECTIONS above for that reason), but a reviewer still
// shouldn't have to manually re-check its table on every model when the underlying
// data is byte-identical. Feature Mapping deliberately does NOT get this chip
// (Sean, 2026-08-27) despite the same "no approve/flag" shape as Campaigns --
// transparency-only, not meant to carry any review signal at all.
export const INVARIANCE_CHECKED_SECTIONS: SectionKeyType[] = [
  ...SUMMARY_SECTIONS,
  SectionKey.CAMPAIGNS
];

export const SECTION_DESCRIPTIONS: Record<SectionKeyType, string> = {
  [SectionKey.PORTFOLIO]: 'Total spend and impressions across all channels over time.',
  [SectionKey.CHANNELS]: 'Spend, impressions, and CPM split by marketing channel and platform.',
  [SectionKey.CAMPAIGNS]: 'Spend and impressions broken down to the campaign level.',
  [SectionKey.FEATURE_MAPPING]:
    'How raw input columns map through platform and tactic to MMM features.',
  [SectionKey.CONTEXT_VARS]: 'Non-marketing inputs the model uses to explain variance.',
  [SectionKey.DEPENDENT_VAR]: 'The target metric the model predicts.'
};

export const EMPTY_DIFFS = {
  summary: { unchanged: 0, new: 0, changed: 0, removed: 0 }
} as const;

export const DIFF_AWARE_SECTIONS: SectionKeyType[] = [
  SectionKey.PORTFOLIO,
  SectionKey.CHANNELS,
  SectionKey.CAMPAIGNS,
  SectionKey.FEATURE_MAPPING,
  SectionKey.CONTEXT_VARS,
  SectionKey.DEPENDENT_VAR
];
